/**
 * YouTube iFrame API Transcript Fetcher
 * Alternative approach using YouTube's embed features
 */

/**
 * Extract video ID from YouTube URL
 * @param {string} url - YouTube URL
 * @returns {string|null} Video ID or null
 */
export function extractVideoId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|m\.youtube\.com\/watch\?v=)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) {
      return match[1]
    }
  }
  return null
}

/**
 * Try to get captions using YouTube's oEmbed + noembed service
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<Object>} Video info with possible caption data
 */
export async function getVideoInfoWithCaptions(videoId) {
  try {
    // Try noembed service which doesn't have CORS issues
    const response = await fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`)
    const data = await response.json()
    
    return {
      title: data.title || 'YouTube Video',
      author: data.author_name || 'Unknown',
      thumbnailUrl: data.thumbnail_url,
      providerUrl: data.provider_url,
      html: data.html // Contains embed code
    }
  } catch (error) {
    console.warn('Failed to get video info from noembed:', error)
    return null
  }
}

/**
 * Fetch transcript using YouTube Transcript API service
 * This uses a public API that fetches YouTube transcripts
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<string>} Transcript text
 */
export async function fetchTranscriptViaAPI(videoId) {
  const services = [
    // YouTube Transcript API endpoints (public services)
    {
      name: 'youtubetranscript.com',
      buildUrl: (id) => `https://youtubetranscript.com/api/transcript?video=${id}`,
      parseResponse: async (response) => {
        const data = await response.json()
        if (data && data.transcript) {
          return data.transcript.map(item => item.text).join(' ')
        }
        return null
      }
    },
    {
      name: 'serpapi transcript',
      buildUrl: (id) => `https://serpapi.com/search.json?engine=youtube_transcripts&video_id=${id}`,
      parseResponse: async (response) => {
        const data = await response.json()
        if (data && data.transcripts) {
          return data.transcripts.map(t => t.text).join(' ')
        }
        return null
      }
    }
  ]
  
  for (const service of services) {
    try {
      const url = service.buildUrl(videoId)
      const response = await fetch(url)
      
      if (response.ok) {
        const transcript = await service.parseResponse(response)
        if (transcript) {
          return cleanTranscript(transcript)
        }
      }
    } catch (error) {
      console.warn(`Service ${service.name} failed:`, error.message)
    }
  }
  
  return null
}

/**
 * Clean transcript text
 * @param {string} text - Raw transcript
 * @returns {string} Cleaned transcript
 */
function cleanTranscript(text) {
  if (!text) return ''
  
  return text
    .replace(/\[Music\]/gi, '')
    .replace(/\[Applause\]/gi, '')
    .replace(/\[Laughter\]/gi, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Alternative: Use YouTube's automatic captions via subtitle downloader
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<string>} Transcript text
 */
export async function fetchAutomaticCaptions(videoId) {
  try {
    // Try to get subtitles via downsub API (no CORS)
    const response = await fetch(`https://downsub.com/?url=https://www.youtube.com/watch?v=${videoId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    
    if (response.ok) {
      const html = await response.text()
      // Parse the HTML to find subtitle links
      const subtitleMatch = html.match(/href="([^"]*\.vtt[^"]*)"/i)
      
      if (subtitleMatch) {
        const vttUrl = subtitleMatch[1]
        const vttResponse = await fetch(vttUrl)
        const vttText = await vttResponse.text()
        
        // Parse VTT format
        return parseVTT(vttText)
      }
    }
  } catch (error) {
    console.warn('Failed to fetch automatic captions:', error)
  }
  
  return null
}

/**
 * Parse VTT (WebVTT) format to extract text
 * @param {string} vttContent - VTT format content
 * @returns {string} Extracted text
 */
function parseVTT(vttContent) {
  // Remove WEBVTT header and metadata
  const lines = vttContent.split('\n')
  let transcript = ''
  let skipNext = false
  
  for (const line of lines) {
    // Skip timecodes (format: 00:00:00.000 --> 00:00:00.000)
    if (line.includes('-->')) {
      skipNext = false
      continue
    }
    
    // Skip cue identifiers (usually numbers)
    if (/^\d+$/.test(line.trim())) {
      continue
    }
    
    // Skip WEBVTT header and empty lines
    if (line.startsWith('WEBVTT') || line.trim() === '') {
      continue
    }
    
    // Skip style and note blocks
    if (line.startsWith('STYLE') || line.startsWith('NOTE')) {
      skipNext = true
      continue
    }
    
    if (skipNext) continue
    
    // Add the actual transcript text
    transcript += line + ' '
  }
  
  return cleanTranscript(transcript)
}

/**
 * Main function to get YouTube transcript with multiple fallbacks
 * @param {string} videoId - YouTube video ID
 * @returns {Promise<Object>} Object with transcript and metadata
 */
export async function getYouTubeTranscript(videoId) {
  const result = {
    videoId: videoId,
    transcript: null,
    metadata: null,
    method: null,
    error: null
  }
  
  try {
    // Get video metadata first
    result.metadata = await getVideoInfoWithCaptions(videoId)
    
    // Method 1: Try transcript API services
    console.log('Trying transcript API services...')
    const apiTranscript = await fetchTranscriptViaAPI(videoId)
    if (apiTranscript) {
      result.transcript = apiTranscript
      result.method = 'api'
      return result
    }
    
    // Method 2: Try automatic captions
    console.log('Trying automatic captions...')
    const autoTranscript = await fetchAutomaticCaptions(videoId)
    if (autoTranscript) {
      result.transcript = autoTranscript
      result.method = 'automatic'
      return result
    }
    
    // Method 3: Try manual extraction (requires user to provide)
    result.error = 'No automatic transcript available. Please check if the video has captions enabled.'
    result.method = 'none'
    
  } catch (error) {
    result.error = error.message
  }
  
  return result
}

/**
 * Generate a manual instruction for users
 * @param {string} videoId - YouTube video ID
 * @returns {string} Instructions for manual transcript extraction
 */
export function getManualInstructions(videoId) {
  return `
## Manual Transcript Extraction

Since automatic extraction failed, you can manually get the transcript:

1. **Open the video**: https://www.youtube.com/watch?v=${videoId}

2. **Click the "..." menu** below the video

3. **Select "Show transcript"**

4. **Copy the transcript text**

5. **Paste it in the text area below**

Alternatively, you can:
- Enable captions on the video
- Use YouTube's built-in transcript feature
- Try a different video with captions enabled
  `
}